// src/lib/token.ts
// Simplified token management - memory-only cache backed by NextAuth session
// NO localStorage, NO window globals - tokens are fetched from session cookie

let cachedToken: string | null = null;
let cachedExpiry: number | null = null;
let inflight: Promise<string | undefined> | null = null;

/**
 * Clear the in-memory token cache.
 * Call this on logout before signOut().
 */
export function clearApiToken(): void {
  cachedToken = null;
  cachedExpiry = null;
  inflight = null;
}

/**
 * Set token directly (used after login to avoid extra session fetch)
 */
export function setApiToken(token: string, expiresAt?: number): void {
  cachedToken = token;
  cachedExpiry = typeof expiresAt === 'number' ? expiresAt : null;
}

/**
 * Fetch token from NextAuth session endpoint
 */
async function fetchTokenFromSession(): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;

  try {
    const res = await fetch('/api/auth/session', {
      cache: 'no-store',
      credentials: 'include',
    });

    if (!res.ok) return undefined;

    const data = await res.json();
    return seedApiTokenFromSession(data);
  } catch {
    return undefined;
  }
}

/**
 * Cache the token from a session object already in hand (the /api/auth/session
 * JSON, which is also what useSession() holds), so a caller that has the session
 * does not fetch it again. Returns the token, or undefined when there is none.
 */
export function seedApiTokenFromSession(data: any): string | undefined {
  const token: string | undefined = data?.user?.accessToken;
  if (!token) return undefined;

  // Extract expiry from session
  let expiry: number | undefined;
  const sessExp = data?.user?.expiresAt ?? data?.expiresAt;
  if (typeof sessExp === 'number') {
    expiry = sessExp;
  } else if (typeof data?.expires === 'string') {
    const t = Date.parse(data.expires);
    if (!Number.isNaN(t)) expiry = Math.floor(t / 1000);
  }

  cachedToken = token;
  cachedExpiry = expiry ?? null;
  return token;
}

/**
 * Get API token for Authorization header.
 * Returns cached token if valid, otherwise fetches from session.
 * Deduplicates concurrent requests.
 */
export async function getApiToken(forceRefresh = false): Promise<string | undefined> {
  // Check cache first
  const now = Math.floor(Date.now() / 1000);
  const isExpired = cachedExpiry !== null && (cachedExpiry - now) < 60;

  if (cachedToken && !forceRefresh && !isExpired) {
    return cachedToken;
  }

  // Deduplicate concurrent fetches
  if (!inflight) {
    inflight = fetchTokenFromSession().finally(() => {
      inflight = null;
    });
  }

  const token = await inflight;
  return token || cachedToken || undefined;
}
